import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useCourse } from "./CourseContext";
import { useUser } from "./UserContext";
import { questionService } from "../services/api";

const TopicsContext = createContext();

export function TopicsProvider({ children }) {
  const { selectedCourse } = useCourse();
  const { user } = useUser();

  const [topics, setTopics] = useState({});
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState(null);

  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [completedLoading, setCompletedLoading] = useState(false);
  const [completedError, setCompletedError] = useState(null);

  const [incompleteQuestions, setIncompleteQuestions] = useState([]);
  const [incompleteLoading, setIncompleteLoading] = useState(false);
  const [incompleteError, setIncompleteError] = useState(null);

  const cacheKeyForCourse = useCallback((crn) => `topics_${crn}`, []);

  const refreshTopics = useCallback(async (crn = selectedCourse?.CRN) => {
    setTopicsError(null);
    if (!crn) {
      setTopics({});
      setTopicsLoading(false);
      return;
    }

    setTopicsLoading(true);
    try {
      const cacheKey = cacheKeyForCourse(crn);
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setTopics(JSON.parse(cached) || {});
      }

      // Use the new optimized endpoint that fetches topics and problems in one call
      const topicsData = await questionService.get(
        `/problems/topics-with-problems/${crn}`
      );

      // topicsData is already in the format { "topic_name": [problems...], ... }
      const safeTopicsData = topicsData && typeof topicsData === 'object' ? topicsData : {};

      setTopics(safeTopicsData);
      sessionStorage.setItem(cacheKeyForCourse(crn), JSON.stringify(safeTopicsData));
    } catch (err) {
      console.error("Error loading topics:", err);
      setTopics({});
      setTopicsError(err?.message || "Failed to load topics");
    } finally {
      setTopicsLoading(false);
    }
  }, [selectedCourse?.CRN, cacheKeyForCourse]);

  const resolveStudentId = useCallback(() => {
    const fromUser = user?.studentID;
    if (fromUser != null && fromUser !== "") {
      return String(fromUser);
    }
    try {
      return localStorage.getItem("studentID");
    } catch {
      return null;
    }
  }, [user?.studentID]);

  const refreshCompletedQuestions = useCallback(async () => {
    setCompletedError(null);
    const student_id = resolveStudentId();
    if (!student_id) {
      setCompletedQuestions([]);
      return;
    }

    setCompletedLoading(true);
    try {
      const completed = await questionService.get(
        `/problems/completed-questions/by-id/${student_id}`
      );
      setCompletedQuestions(Array.isArray(completed) ? completed : []);
    } catch (err) {
      console.error("Error loading completed questions:", err);
      setCompletedQuestions([]);
      setCompletedError(err?.message || "Failed to load completed questions");
    } finally {
      setCompletedLoading(false);
    }
  }, [resolveStudentId]);

  const refreshIncompleteQuestions = useCallback(async () => {
    setIncompleteError(null);
    const student_id = resolveStudentId();
    if (!student_id) {
      setIncompleteQuestions([]);
      return;
    }

    setIncompleteLoading(true);
    try {
      const incompleted = await questionService.getIncompleteQuestionsByStudentId(student_id);
      setIncompleteQuestions(Array.isArray(incompleted) ? incompleted : []);
    } catch (err) {
      console.error("Error loading incompleted questions:", err);
      setIncompleteQuestions([]);
      setIncompleteError(err?.message || "Failed to load incomplete questions");
    } finally {
      setIncompleteLoading(false);
    }
  }, [resolveStudentId]);

  useEffect(() => {
    refreshTopics();
  }, [refreshTopics]);

  useEffect(() => {
    refreshCompletedQuestions();
    refreshIncompleteQuestions();
  }, [refreshCompletedQuestions, refreshIncompleteQuestions]);

  const value = useMemo(
    () => ({
      topics,
      topicsLoading,
      topicsError,
      refreshTopics,
      completedQuestions,
      setCompletedQuestions,
      completedLoading,
      completedError,
      refreshCompletedQuestions,
      incompleteQuestions,
      setIncompleteQuestions,
      incompleteLoading,
      incompleteError,
      refreshIncompleteQuestions
    }),
    [
      topics,
      topicsLoading,
      topicsError,
      refreshTopics,
      completedQuestions,
      completedLoading,
      completedError,
      refreshCompletedQuestions,
      incompleteQuestions,
      incompleteLoading,
      incompleteError,
      refreshIncompleteQuestions
    ]
  );

  return <TopicsContext.Provider value={value}>{children}</TopicsContext.Provider>;
}

export function useTopics() {
  return useContext(TopicsContext);
}
